/* eslint-disable @typescript-eslint/no-explicit-any */
import { isLightColor, Text, Tooltip, Stack } from "@mantine/core"
import { getStripedGradientRuleFromColor } from "../../../../../utils/getStripedGradientRule"
import { FormattedText } from "../../../../../@client/Theasoft_Framework"
import { TsCellStyle } from "../../../../../@client/Theasoft_Core"
import { Cell, SimpleCastDto } from "@/orval/gen/model"
import { CSSProperties, ReactNode } from "react"
import { colorToHex } from "@/utils/color-to-rgba"

const contentCache = new WeakMap<Cell, ReactNode>()

const baseItemStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: "1",
  flex: "1",
  minWidth: 0,
  overflow: "hidden",
}

function renderCellText(
  text: FormattedText[],
  style: TsCellStyle,
  casts: SimpleCastDto[] | undefined
) {
  return text?.map((v, i) => {
    const bg = style?.backColor
      ? colorToHex(style.backColor)
      : (casts?.[i]?.castBackColor ?? undefined)
    const c = bg
      ? isLightColor(bg)
        ? "black"
        : "var(--mantine-color-dark-0)"
      : undefined
    return (
      <div
        key={i}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
        }}
      >
        <Text
          truncate
          span
          size="xs"
          style={{ alignSelf: "center", flex: 1, maxWidth: "100%" }}
          fs={v.format?.font.italic ? "italic" : undefined}
          fw={v.format?.font.bold ? "bold" : undefined}
          bg={bg}
          c={c}
        >
          {i > 0 ? " " : null}
          {v.text + ""}
        </Text>
      </div>
    )
  })
}

export function renderContent(cell: Cell) {
  const cached = contentCache.get(cell)
  if (cached) {
    return cached
  }

  const dutyRosters = cell.dutyRosters?.flatMap((v) =>
    v.shifts?.map((s, i) =>
      s.label ? (
        <div
          key={i}
          style={{
            ...baseItemStyle,
            backgroundColor: s.bg as any,
            color:
              s.foreColor ?? (s.bg && isLightColor(s.bg) ? "black" : undefined),
          }}
        >
          {s.label}
        </div>
      ) : null
    )
  )

  const pendingAbsences = cell.absences?.filter((v) => v.isPending) ?? []
  const confirmedAbsences = cell.absences?.filter((v) => !v.isPending) ?? []

  const pendingBars = pendingAbsences.map((v, i) => {
    const bg = v.bg
      ? getStripedGradientRuleFromColor(
          v.bg,
          "light-dark(var(--mantine-color-gray-2),var(--mantine-color-dark-5))",
          2,
          45
        )
      : getStripedGradientRuleFromColor(
          "light-dark(var(--mantine-color-gray-4),var(--mantine-color-dark-3))",
          "light-dark(var(--mantine-color-gray-2),var(--mantine-color-dark-5))",
          2,
          45
        )
    return (
      <Tooltip
        key={`pending-${i}`}
        withinPortal
        label={
          <Text
            size="xs"
            style={{
              backgroundColor: v.bg ?? undefined,
              color: v.bg && isLightColor(v.bg) ? "black" : undefined,
              padding: "0 4px",
            }}
          >
            {v.name}
          </Text>
        }
      >
        <div
          style={{
            position: "absolute",
            top: i * 3,
            left: 0,
            right: 0,
            height: 4,
            background: bg,
            zIndex: 1,
            cursor: "default",
          }}
        />
      </Tooltip>
    )
  })

  const confirmedAbsenceItems = confirmedAbsences.map((v, i) => (
    <div
      key={i}
      style={{
        ...baseItemStyle,
        background: v.bg || undefined,
        color: v.bg && isLightColor(v.bg) ? "black" : undefined,
      }}
    >
      {v.name}
    </div>
  ))

  const overflowCount =
    cell.overflowDutyRosters?.reduce(
      (sum, dr) => sum + (dr.shifts?.length ?? 0),
      0
    ) ?? 0

  const content = (
    <div
      style={{
        position: "relative",
        display: "flex",
        height: "100%",
        width: "100%",
        paddingTop: pendingBars.length > 0 ? pendingBars.length * 3 : undefined,
      }}
    >
      {pendingBars}
      {dutyRosters}
      {confirmedAbsenceItems}
      {cell.text
        ? renderCellText(cell.text! as any, null as any, cell.casts)
        : null}
      {overflowCount > 0 ? (
        <Tooltip
          withinPortal
          label={
            <Stack gap={2}>
              {cell.overflowDutyRosters?.flatMap((dr) =>
                dr.shifts?.map((s, i) => (
                  <Text
                    key={i}
                    size="xs"
                    style={{
                      backgroundColor: s.bg ?? undefined,
                      color:
                        s.foreColor ??
                        (s.bg && isLightColor(s.bg) ? "black" : undefined),
                      padding: "0 4px",
                    }}
                  >
                    {s.start && s.end
                      ? `${s.start.substring(0, 5)}-${s.end.substring(0, 5)} `
                      : null}
                    {s.label}
                  </Text>
                ))
              )}
            </Stack>
          }
        >
          <div
            style={{
              ...baseItemStyle,
              flex: "0 0 auto",
              fontSize: "0.5rem",
              paddingInline: 1,
              cursor: "default",
              position: "absolute",
              top: -1,
              right: -1,
              backgroundColor: "var(--mantine-color-body)",
              color: "var(--mantine-color-text)",
              border: "1px solid var(--mantine-color-gray-7)",
              borderRadius: "0 0 0 4px",
            }}
          >
            +{overflowCount}
          </div>
        </Tooltip>
      ) : null}
    </div>
  )

  contentCache.set(cell, content)
  return content
}
